<template>
  <TabList
    :unstyled="true"
    :pt="passThrough"
    class="meridia-tab-list"
    v-bind="$attrs"
  >
    <slot />
  </TabList>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';
import TabList from 'primevue/tablist';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import type { TabListPassThroughOptions } from 'primevue/tablist';
import {
  MERIDIA_TABS_ACTIVE_VALUE,
  MERIDIA_TABS_ORDER,
} from 'src/design_system/components/tab/types';
import type { MeridiaTabListProps } from 'src/design_system/components/tab/types';

const props = defineProps<MeridiaTabListProps>();

defineSlots<{ default: () => any }>();

// Active value + tab order come from MeridiaTabs (the same order the slide transitions use), so the
// active-bar geometry below needs no slot or DOM introspection.
const activeValue = inject(MERIDIA_TABS_ACTIVE_VALUE, ref<string | number>(''));
const tabOrder = inject(MERIDIA_TABS_ORDER, ref<(string | number)[]>([]));

const tabCount = computed(() => tabOrder.value.length || 1);
const activeIndex = computed(() => {
  const i = tabOrder.value.findIndex((v) => v == activeValue.value); // loose, matches PrimeVue
  return i < 0 ? 0 : i;
});

const passThrough = computed(
  () =>
    usePassThrough(
      props.pt,
      {
        root: {
          class: 'meridia-tab-list-root flex w-full h-12',
        },
        tabList: {
          class:
            'meridia-tab-list-nav relative flex flex-1 gap-0 list-none m-0 p-0',
        },
        activeBar: {
          // Geometry lives in CSS so it re-resolves on any resize (sidebar collapse, zoom) with
          // zero JS. Tabs are equal width (flex-1), so the bar is a fraction of the nav driven by
          // two custom properties set here. The Tailwind `!` utilities are stylesheet rules with
          // !important, so they beat PrimeVue's imperative inline pixels from updateInkBar()
          // (which only runs on mount/tab-change, never on resize). transition-all keeps the
          // slide on tab change and smooths the resize.
          class:
            'meridia-tab-list-active-bar absolute top-0 h-1 bg-primary transition-all duration-200 ' +
            '!w-[calc(100%/var(--tab-count))] !left-[calc(var(--active-index)*100%/var(--tab-count))]',
          style: {
            '--tab-count': tabCount.value,
            '--active-index': activeIndex.value,
          },
        },
        content: {
          class:
            'meridia-tab-list-content flex flex-1 overflow-x-auto overflow-y-hidden h-full',
        },
        prevButton: {
          class: 'meridia-tab-list-prev',
        },
        nextButton: {
          class: 'meridia-tab-list-next',
        },
      } as TabListPassThroughOptions,
      {
        mergeSections: false,
        mergeProps: true,
      }
    ) as PassThrough<TabListPassThroughOptions>
);
</script>
